import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close as CloseIcon, EditOutlined as EditIcon } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { INDIGO, SLATE } from "../../constants/Colors";
import { usePageMeta } from "../../contexts/PageMetaContext";
import useApiCall from "../../hooks/useApiCall";
import LoadingSpinner from "../../components/LoadingSpinner";
import { dateFormatter } from "../../utils";
import { USER_ROLES } from "../../constants/Constants";
import TrimTextField from "../../components/TrimTextField";

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};

const FIELD_LABEL_SX = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: SLATE[700],
  mb: "6px",
  display: "block",
};

export default function Users() {
  const tokens = useSelector((state) => state?.tokens?.value);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();

  const [users, setUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", user_type: "user" });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    setPageMeta({ description: "Manage platform users and their access roles." });
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    makeRequest("/v1/users", { method: "GET", authTokens: tokens })
      .then((data) => {
        if (data && !Array.isArray(data) && Array.isArray(data.users)) {
          setUsers(data.users);
          if (Array.isArray(data.roles)) {
            setAvailableRoles(
              data.roles.map((rv) => USER_ROLES.find((r) => r.value === rv) || { value: rv, title: rv })
            );
          }
        } else if (Array.isArray(data)) {
          setUsers(data);
          setAvailableRoles(USER_ROLES);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasChanges = editingUser
    ? form.name.trim() !== (editingUser.name || "") || form.user_type !== (editingUser.user_type || "user")
    : false;

  const openEditDialog = (user) => {
    setEditingUser(user);
    setForm({ name: user.name || "", email: user.email || "", user_type: user.user_type || "user" });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setForm({ name: "", email: "", user_type: "user" });
    setFormErrors({});
  };

  const handleSubmit = async () => {
    const errors = {};
    if (!form.name.trim()) errors.name = true;
    if (!form.email.trim()) errors.email = true;
    if (!form.user_type) errors.user_type = true;
    if (Object.keys(errors).length) { setFormErrors(errors); return; }

    setSaving(true);
    try {
      const response = await makeRequest("/v1/users/upsert", {
        method: "POST",
        body: { users: [{ id: editingUser.id, ...form }] },
        authTokens: tokens,
      });
      if (Array.isArray(response) && response.length > 0) {
        const upserted = response[0];
        setUsers((prev) => prev.map((u) => (u.id === upserted.id ? upserted : u)));
      }
      handleDialogClose();
    } catch {
      // error already toasted
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading users..." subMessage="Fetching platform users" />;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Card */}
      <Box sx={{ bgcolor: "#ffffff", border: `1px solid ${SLATE[200]}`, borderRadius: "16px", overflow: "hidden" }}>

        {/* Card header */}
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${SLATE[200]}` }}>
          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: SLATE[900] }}>
            Platform Users
          </Typography>
        </Box>

        {users.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Typography sx={{ fontSize: "2rem", mb: 1 }}>👥</Typography>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900], mb: "6px" }}>No users yet</Typography>
            <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>No platform users have been added yet.</Typography>
          </Box>
        ) : (
          <Box>
            {/* Column headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "200px 1fr 1fr 1fr 1fr 60px",
                px: 3,
                py: 1,
                borderBottom: `1px solid ${SLATE[200]}`,
                bgcolor: SLATE[50],
              }}
            >
              {["Name", "Email", "Role", "Created", "Last Login", "Action"].map((col) => (
                <Typography key={col} sx={{ fontSize: "0.6875rem", fontWeight: 600, color: SLATE[500], textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {col}
                </Typography>
              ))}
            </Box>

            {users.map((user, idx) => (
              <Box
                key={user.id || idx}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "200px 1fr 1fr 1fr 1fr 60px",
                  px: 3,
                  py: 2,
                  alignItems: "center",
                  borderBottom: idx < users.length - 1 ? `1px solid ${SLATE[100]}` : "none",
                  "&:hover": { bgcolor: SLATE[50] },
                }}
              >
                <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: SLATE[900] }}>
                  {user.name || "—"}
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", color: SLATE[600] }}>{user.email || "—"}</Typography>
                <Typography sx={{ fontSize: "0.875rem", color: SLATE[700], fontWeight: 500 }}>
                  {USER_ROLES.find((r) => r.value === user.user_type)?.title || user.user_type || "—"}
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", color: SLATE[600] }}>
                  {user.created_at && user.created_at !== "-" ? dateFormatter(user.created_at, "MM/DD/YYYY") : "—"}
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", color: SLATE[600] }}>
                  {user.last_login_at && user.last_login_at !== "-" ? dateFormatter(user.last_login_at, "MM/DD/YYYY HH:mm AMPM") : "—"}
                </Typography>
                <Box>
                  <Tooltip title="Edit" placement="top">
                    <IconButton
                      size="small"
                      onClick={() => openEditDialog(user)}
                      sx={{ color: SLATE[400], "&:hover": { color: INDIGO[600], bgcolor: INDIGO[50] } }}
                    >
                      <EditIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>Edit User</Typography>
          <IconButton size="small" onClick={handleDialogClose} sx={{ color: SLATE[400] }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: "8px !important", display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Full Name</Typography>
            <TrimTextField
              fullWidth size="small" placeholder="e.g. Jane Smith"
              value={form.name}
              onChange={(e) => { setForm((p) => ({ ...p, name: e.target.value })); setFormErrors((p) => ({ ...p, name: false })); }}
              error={formErrors.name}
              helperText={formErrors.name ? "Name is required" : ""}
              sx={INPUT_SX}
            />
          </Box>

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Email Address</Typography>
            <TextField
              fullWidth size="small"
              value={form.email}
              disabled
              sx={{ ...INPUT_SX, "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: SLATE[500], cursor: "not-allowed" } }}
            />
          </Box>

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Role</Typography>
            <Autocomplete
              size="small"
              options={availableRoles}
              value={availableRoles.find((r) => r.value === form.user_type) || null}
              getOptionLabel={(o) => o.title}
              disableClearable
              onChange={(_, val) => { setForm((p) => ({ ...p, user_type: val?.value || "" })); setFormErrors((p) => ({ ...p, user_type: false })); }}
              renderInput={(params) => <TextField {...params} error={formErrors.user_type} helperText={formErrors.user_type ? "Role is required" : ""} sx={INPUT_SX} />}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}>
            <Button onClick={handleDialogClose} variant="outlined" size="small" sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none", "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving || !hasChanges} variant="contained" size="small" sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}>
              {saving && <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />}
              Save Changes
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
